import type { ReactNode } from 'react';
import type {
  DefenseTally,
  ExemptGroundingRow,
  ProvenanceClaim,
  ProvenanceManifest,
  ProvenanceSource,
  SourceQuote as SourceQuoteData,
} from '@/features/dossier/api';

/** `section_19_performance_testing` -> `§19 · Performance Testing`;
 *  `module_1_administrative` -> `Module 1 · Administrative`. */
function humanizeSectionKey(key: string | null | undefined): string {
  if (!key) return '';
  const titleize = (s: string) =>
    s
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  const sec = key.match(/^section_(\d+)_(.+)$/);
  if (sec && sec[1] !== undefined && sec[2] !== undefined) {
    return `§${Number(sec[1])} · ${titleize(sec[2])}`;
  }
  const mod = key.match(/^module_(\d+)_(.+)$/);
  if (mod && mod[1] !== undefined && mod[2] !== undefined) {
    return `Module ${Number(mod[1])} · ${titleize(mod[2])}`;
  }
  // Any other snake_case key (e.g. an EU device's manufacturer_and_authorised_representative)
  // reads as words — never a raw key, never a US "§" number.
  if (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(key)) {
    const words = key.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return key;
}

// USA 510(k)-exempt ONLY: a small label map (not a blind titleize) so the
// FDA-specific verifier keys read naturally to a reviewer. Falls back to a
// titleized key for any row this map hasn't been updated for yet.
const EXEMPT_GROUNDING_LABELS: Record<string, string> = {
  threshold_test: 'Threshold test (8xx.9 limitations of exemption)',
  named_comparator: 'Named comparator device',
  rx_otc_determination: 'Prescription vs. OTC determination',
  summary_malfunction: 'Summary-malfunction reporting',
  exemption_grounding: 'Exemption determination vs. FDA record',
};

function exemptGroundingLabel(key: string): string {
  return (
    EXEMPT_GROUNDING_LABELS[key] ??
    key
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

function TierBadge({ tier }: { tier: 'exact' | 'contextual' }) {
  return tier === 'exact' ? (
    <span className="text-[13px] px-1.5 py-0.5 rounded bg-black/5 text-navy border font-medium whitespace-nowrap">
      traced
    </span>
  ) : (
    <span className="text-[13px] px-1.5 py-0.5 rounded bg-black/5 text-muted border whitespace-nowrap">
      generated
    </span>
  );
}

function VerifyBadge({ status, label }: { status: string; label?: string }) {
  if (status === 'recognized' || status === 'verified' || status === 'registered' || status === 'designated') {
    return (
      <span className="text-[13px] px-1.5 py-0.5 rounded bg-state-success-bg text-state-success-text border border-state-success-border whitespace-nowrap">
        ✓ {label ?? status}
      </span>
    );
  }
  if (status === 'recognised_international') {
    // AU: a recognised international standard cited as evidence — neither a
    // register-pass nor a failure; informational/neutral.
    return (
      <span className="text-[13px] px-1.5 py-0.5 rounded bg-black/5 text-muted border whitespace-nowrap">
        {label ?? "recognised int'l"}
      </span>
    );
  }
  if (status === 'not_designated') {
    // UK: a definite result (checked, not on the designated list) — not a failure.
    return (
      <span className="text-[13px] px-1.5 py-0.5 rounded bg-black/5 text-muted border whitespace-nowrap">
        {label ?? 'not designated'}
      </span>
    );
  }
  if (status === 'unverified') {
    // EU: a verified "not harmonised" (label) reads as a definite result, not a
    // failure to check. US: plain "unverified" (DB unreachable).
    return (
      <span className="text-[13px] px-1.5 py-0.5 rounded bg-black/5 text-muted border whitespace-nowrap">
        {label ?? 'unverified'}
      </span>
    );
  }
  if (status === 'repealed') {
    // a cited act no longer in force — a specific, harder warning than "for review"
    return (
      <span className="text-[13px] px-1.5 py-0.5 rounded bg-state-error-bg text-state-error-text border border-state-error-border whitespace-nowrap">
        ⚠ repealed
      </span>
    );
  }
  // review -> a finding to look at (not an absolute "wrong")
  return (
    <span className="text-[13px] px-1.5 py-0.5 rounded bg-state-error-bg text-state-error-text border border-state-error-border whitespace-nowrap">
      ⚠ for review
    </span>
  );
}

// One uniform tally line for standards / CFR / predicates so they read the same.
// `unverifiedWord` lets the EU say "not harmonised" where the US says "unverified".
function VerifySummary({
  tally,
  word,
  unverifiedWord = 'unverified',
}: {
  tally: DefenseTally;
  word: string;
  unverifiedWord?: string;
}) {
  return (
    <>
      <span className="font-semibold">{tally.verified}</span> of {tally.total} {word}
      {tally.flagged > 0 && (
        <span className="font-medium text-state-error-text">
          {' '}· {tally.flagged} for review
        </span>
      )}
      {tally.unverified > 0 && (
        <span className="text-muted"> · {tally.unverified} {unverifiedWord}</span>
      )}
    </>
  );
}

// items needing attention first — the rows a submitter must act on
function _verifyRank(status: string): number {
  if (status === 'recognized' || status === 'verified' || status === 'registered' || status === 'recognised_international' || status === 'designated') return 2;
  if (status === 'unverified' || status === 'not_designated') return 1;
  return 0; // review / outdated
}

// surface the richest evidence first: page-anchored statements (upload + page),
// then account-anchored statements, then field/value claims.
function claimRank(c: ProvenanceClaim): number {
  if (c.locator) return 2;
  if (c.element === 'Statement') return 1;
  return 0;
}

/**
 * The authority of a traced claim — the distinction that matters when defending
 * to FDA. `external` = independently substantiated (an uploaded study document
 * or the FDA-verified predicate). `input` = faithfully carried from what the
 * applicant entered (product / company account) — proves no drift/fabrication,
 * but is self-asserted, not external proof.
 */
function claimAuthority(
  c: ProvenanceClaim,
  sourceById: Map<string, ProvenanceSource>,
): 'external' | 'input' {
  const kind = c.source_id ? sourceById.get(c.source_id)?.kind : undefined;
  if (kind === 'upload' || kind === 'supporting_doc' || kind === 'company_doc' || kind === 'predicate') {
    return 'external';
  }
  const id = c.source_id ?? '';
  if (
    id.startsWith('UPLOAD:') ||
    id.startsWith('SUPPORTING_DOC:') ||
    id.startsWith('COMPANY_DOC:') ||
    id.startsWith('PREDICATE')
  ) {
    return 'external';
  }
  return 'input';
}

/**
 * The single row primitive every section renders through. A row is: an optional
 * muted kicker (what kind of thing this is), a bold headline (the identifier OR
 * the value/statement — both render the same), an optional muted provenance
 * sub-line, optional quote callouts, and a status badge pinned to the right.
 *
 * Row shape is independent of the data, so a device 510(k) eSTAR (short
 * identifiers, named fields) and a drug/biologic eCTD (long free-text
 * statements) render identically across every product type and pathway.
 */
function MatrixRow({
  kicker,
  headline,
  sub,
  badge,
  children,
}: {
  kicker?: ReactNode;
  headline: ReactNode;
  sub?: ReactNode;
  badge?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-t py-3">
      <div className="min-w-0 flex-1">
        {kicker && <div className="mb-0.5 text-[12px] text-muted">{kicker}</div>}
        <div className="text-[15px] font-medium text-navy whitespace-pre-line leading-snug">
          {headline}
        </div>
        {sub && <div className="mt-1 text-[13px] text-muted">{sub}</div>}
        {children}
      </div>
      {badge && <div className="shrink-0 pt-0.5">{badge}</div>}
    </div>
  );
}

/** The RA-requested "Source Quote" callout — one box per place a standard / CFR
 *  is cited, showing the exact dossier sentence and where it came from. */
function SourceQuote({ quotes }: { quotes: SourceQuoteData[] }) {
  // The backend serves defense quotes as { section, excerpts: string[] }, even
  // though the type is SourceQuote ({ in, text }). Reconcile BOTH shapes, flatten
  // to one row per excerpt, and skip empties. (Reading only q.text/q.in made
  // every standard quote look empty, so they were all dropped.)
  const rows = (quotes ?? []).flatMap((q) => {
    const raw = q as unknown as {
      section?: string;
      excerpts?: string[];
      in?: string;
      text?: string;
    };
    const section = raw.section ?? raw.in ?? '';
    const texts = raw.excerpts ?? (raw.text ? [raw.text] : []);
    return texts
      .filter((t) => (t ?? '').trim() !== '')
      .map((text) => ({ section, text }));
  });
  if (rows.length === 0) return null;
  return (
    <>
      {rows.map((r, qi) => {
        const section = humanizeSectionKey(r.section);
        return (
          <div
            key={qi}
            className="mt-1.5 rounded-r border-l-2 border-navy/20 bg-black/[0.025] pl-3 pr-2.5 py-2"
          >
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
                Source Quote
              </span>
              <span className="inline-flex items-center rounded border bg-white px-1.5 py-0.5 text-[12px] font-medium text-navy whitespace-nowrap">
                Dossier{section ? ` · ${section}` : ''}
              </span>
            </div>
            <div className="text-[14px] italic text-navy leading-snug">
              &ldquo;{r.text}&rdquo;
            </div>
          </div>
        );
      })}
    </>
  );
}

/** A small click-through link rendered next to a verification badge. */
function RecordLink({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[13px] underline text-navy"
    >
      link
    </a>
  );
}

function EvidenceTable({ claims }: { claims: ProvenanceClaim[] }) {
  return (
    <div>
      {claims.map((c, i) => (
        <MatrixRow
          key={i}
          kicker={c.element && c.element !== 'Statement' ? c.element : undefined}
          headline={c.value}
          sub={
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {c.locator ? (
                <>
                  <span className="rounded border bg-black/5 px-1.5 py-0.5 font-mono font-medium text-navy">
                    {c.locator}
                  </span>
                  {c.context && (
                    <span className="text-muted">
                      under &ldquo;{c.context}&rdquo;
                    </span>
                  )}
                </>
              ) : (
                c.source_field && <span className="text-muted">{c.source_field}</span>
              )}
              {c.note && <span className="text-muted">· {c.note}</span>}
            </span>
          }
          badge={<TierBadge tier={c.tier} />}
        />
      ))}
    </div>
  );
}

/**
 * Traceability Matrix — a regulator-facing, country-anchored record of where a
 * generated dossier's content comes from.
 *
 * Renders the frozen provenance manifest: the regulatory framing (FDA RTM / EU
 * GSPR), regulatory verification (standards/CFR vs the authorities), value-level
 * **evidence** split by authority (externally evidenced vs faithfully carried
 * from your inputs), the unsourced-statement worklist, section coverage, and the
 * source registry. Honest throughout — `exact` is provable, `contextual` says
 * "generated using"; unfilled fields are omitted, not faked. Renders nothing for
 * legacy / in-flight runs.
 */
export function TraceabilityMatrix({
  manifest,
}: {
  manifest: ProvenanceManifest | null;
}) {
  if (
    !manifest ||
    (manifest.sources.length === 0 && manifest.claims.length === 0)
  ) {
    return null;
  }

  const { framework: fw } = manifest;

  const sourceById = new Map<string, ProvenanceSource>(
    manifest.sources.map((s) => [s.source_id, s]),
  );

  const bySection = new Map<
    string,
    { source: ProvenanceSource | undefined; tier: 'exact' | 'contextual' }[]
  >();
  for (const link of manifest.section_links) {
    const arr = bySection.get(link.section_key) ?? [];
    arr.push({ source: sourceById.get(link.source_id), tier: link.tier });
    bySection.set(link.section_key, arr);
  }
  const sections = [...bySection.keys()].sort();

  // Split evidence by authority — the line an RA must see when defending to FDA.
  const sortedClaims = [...manifest.claims].sort(
    (a, b) => claimRank(b) - claimRank(a),
  );
  const externalClaims = sortedClaims.filter(
    (c) => claimAuthority(c, sourceById) === 'external',
  );
  const inputClaims = sortedClaims.filter(
    (c) => claimAuthority(c, sourceById) === 'input',
  );

  // EU standards check reads differently: "harmonised / not harmonised" (both are
  // verified results), not "recognized / unverified". Detected from the source.
  const euStandards = (manifest.defense?.standards?.[0]?.source ?? '').includes('OJEU');
  // AU standards: cited recognised international standards — informational, not
  // pass/fail against a register. Show an informational summary, not VerifySummary.
  const auStandards = (manifest.defense?.standards ?? []).some(
    (s) => s.status === 'recognised_international',
  );
  // UK standards: "designated / not_designated" (both definite results vs the
  // gov.uk register), not "recognized / unverified".
  const ukStandards = (manifest.defense?.standards ?? []).some(
    (s) => s.status === 'designated' || s.status === 'not_designated',
  );
  // For UK, recompute the tally from the standards array so that `not_designated`
  // lands in `unverified` (neutral) rather than `flagged` (red "for review").
  const ukStandardsTally = ukStandards
    ? (() => {
        const stds = manifest.defense?.standards ?? [];
        return {
          total: stds.length,
          verified: stds.filter((s) => s.status === 'designated').length,
          flagged: 0,
          unverified: stds.filter((s) => s.status === 'not_designated').length,
        };
      })()
    : null;

  // At-a-glance scorecard for the top strip.
  const d = manifest.defense;
  const tracedCount = manifest.claims.length;
  const verifiedCount = d
    ? d.summary.standards.verified +
      d.summary.regulations.verified +
      d.summary.predicates.verified
    : 0;
  const reviewCount =
    (d
      ? d.summary.standards.flagged +
        d.summary.regulations.flagged +
        (d.pathway?.findings.length ?? 0)
      : 0) + (manifest.unsourced?.length ?? 0);

  return (
    <section className="mt-[22px] border rounded-xl overflow-hidden">
      {/* Header — country-anchored regulatory framing */}
      <header className="px-[22px] py-[16px] border-b bg-black/[0.02]">
        <h3 className="m-0 text-[19px] font-bold text-navy">
          {fw?.matrix_name ?? 'Traceability Matrix'}
        </h3>
        <p className="m-0 mt-0.5 text-[14px] text-muted">
          {fw?.label ?? 'Submission Traceability'}
          {fw?.authority ? ` · ${fw.authority}` : ''}
        </p>
      </header>

      {/* At-a-glance: what this is + the scorecard */}
      <div className="px-[22px] py-[16px] border-b">
        <p className="m-0 mb-2.5 text-[14px] text-navy max-w-3xl">
          Every claim below is traced back to its source
          {d?.available
            ? ', and every citation is checked against the relevant authority’s own databases.'
            : '.'}
        </p>
        <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-[14px]">
          <span className="text-navy">
            <span className="text-[17px] font-bold">{tracedCount}</span> claims
            traced to a source
          </span>
          <span className="text-navy">
            <span className="text-[17px] font-bold">{verifiedCount}</span> citations
            verified
          </span>
          {reviewCount > 0 && (
            <span className="text-state-error-text">
              <span className="text-[17px] font-bold">{reviewCount}</span> need
              review
            </span>
          )}
        </div>
      </div>

      {/* Evidence — value-level provable claims, split by defense authority */}
      {manifest.claims.length > 0 && (
        <div className="px-[22px] py-[16px] border-b">
          <div className="text-[16px] font-bold text-navy mb-1">
            Evidence — where each claim comes from ({manifest.claims.length})
          </div>
          <p className="text-[13px] text-muted mb-4 max-w-3xl">
            Each value below is matched back to its source — {externalClaims.length}{' '}
            backed by source documents · {inputClaims.length} from your submitted data.
          </p>

          {externalClaims.length > 0 && (
            <div className="mb-5">
              <div className="text-[15px] font-semibold text-navy">
                Backed by source documents
              </div>
              <p className="text-[13px] text-muted mb-2 max-w-3xl">
                {/* The market's framework names its verified reference (US: FDA-verified
                    predicate / reference); a market without one never borrows FDA wording. */}
                Independently substantiated by an uploaded study document
                {fw?.reference_label ? ` or the ${fw.reference_label}` : ''} — the defensible core.
              </p>
              <EvidenceTable claims={externalClaims} />
            </div>
          )}

          {inputClaims.length > 0 && (
            <div>
              <div className="text-[15px] font-semibold text-navy">
                From your submitted data
              </div>
              <p className="text-[13px] text-muted mb-2 max-w-3xl">
                Faithfully carried from the product / company account — no drift or
                fabrication, but self-asserted, not external proof.
              </p>
              <EvidenceTable claims={inputClaims} />
            </div>
          )}
        </div>
      )}

      {/* Regulatory verification — cited claims checked against authority */}
      {manifest.defense &&
        (!manifest.defense.available ||
          manifest.defense.standards.length > 0 ||
          manifest.defense.regulations.length > 0 ||
          (manifest.defense.predicates?.length ?? 0) > 0 ||
          !!manifest.defense.classification?.part) && (
          <div className="px-[22px] py-[16px] border-b">
            <div className="text-[16px] font-bold text-navy mb-2">
              Regulatory verification
              {manifest.defense.authority ? ` · ${manifest.defense.authority}` : ''}
            </div>

            {/* CA: Schedule 1 classification — cited rule real + class valid? */}
            {manifest.defense.classification?.part && (
              <div className="mb-3 text-[14px] text-navy">
                <span className="font-semibold">Classification check</span> —{' '}
                {manifest.defense.classification.status === 'verified' ? (
                  <span className="text-state-success-text">
                    cites Part {manifest.defense.classification.part}, Rule{' '}
                    {manifest.defense.classification.rule} ✓
                  </span>
                ) : (
                  <span className="font-semibold text-state-error-text">
                    {manifest.defense.classification.note}
                  </span>
                )}
              </div>
            )}

            {/* EU medicinal: legal-basis check — recognised + valid for the product family? */}
            {manifest.defense.legal_basis?.basis && (
              <div className="mb-3 text-[14px] text-navy">
                <span className="font-semibold">Legal basis check</span> —{' '}
                {manifest.defense.legal_basis.status === 'verified' ? (
                  <span className="text-state-success-text">
                    {manifest.defense.legal_basis.label ??
                      manifest.defense.legal_basis.basis}{' '}
                    ✓
                  </span>
                ) : (
                  <span className="font-semibold text-state-error-text">
                    {manifest.defense.legal_basis.note}
                  </span>
                )}
              </div>
            )}

            {!manifest.defense.available && (
              <p className="text-[14px] text-muted max-w-3xl">
                Authoritative verification isn&rsquo;t wired for this jurisdiction
                yet — cited standards and regulations appear in the dossier but
                aren&rsquo;t checked against an external register here.
              </p>
            )}

            {manifest.defense.pathway && (
              <div className="mb-3 text-[14px] text-navy">
                <span className="font-semibold">Pathway check</span> —{' '}
                {manifest.defense.pathway.findings.length === 0 &&
                manifest.defense.pathway.expected_cited ? (
                  <span className="text-state-success-text">
                    cites the expected{' '}
                    {manifest.defense.pathway.expected.join(', ')} ✓
                  </span>
                ) : (
                  <span className="font-semibold text-state-error-text">
                    {manifest.defense.pathway.findings.length > 0
                      ? `${manifest.defense.pathway.findings.length} citation(s) belong to a different pathway`
                      : `expected ${manifest.defense.pathway.expected.join(', ')} not cited`}
                  </span>
                )}
                {manifest.defense.pathway.findings.map((f, i) => (
                  <span key={i} className="block text-[13px] text-muted">
                    {f.cfr} — {f.note}
                  </span>
                ))}
              </div>
            )}

            {manifest.defense.standards.length > 0 && (
              <div className="mb-4">
                <div className="text-[15px] font-semibold text-navy mb-2">
                  Cited standards vs{' '}
                  {manifest.defense.standards[0]?.source ??
                    'the recognized-standards register'}{' '}
                  —{' '}
                  {auStandards ? (
                    <span>
                      <span className="font-semibold">{manifest.defense.standards.length}</span>{' '}
                      recognised international standard(s) cited · TGA recognition basis to confirm
                    </span>
                  ) : (
                    <VerifySummary
                      tally={ukStandards && ukStandardsTally ? ukStandardsTally : manifest.defense.summary.standards}
                      word={euStandards ? 'harmonised' : ukStandards ? 'designated' : 'recognized'}
                      unverifiedWord={euStandards ? 'not harmonised' : ukStandards ? 'not designated' : 'unverified'}
                    />
                  )}
                </div>
                <div>
                  {[...manifest.defense.standards]
                    // EU/AU have no "for review" status, so surface recognised ✓
                    // first (descending); US keeps problems-first (ascending).
                    .sort((a, b) =>
                      euStandards || auStandards || ukStandards
                        ? _verifyRank(b.status) - _verifyRank(a.status)
                        : _verifyRank(a.status) - _verifyRank(b.status),
                    )
                    .map((s, i) => (
                      <MatrixRow
                        key={i}
                        headline={s.designation}
                        sub={
                          (s.recognition_number || s.note) && (
                            <>
                              {s.recognition_number && (
                                <span className="font-mono whitespace-nowrap">
                                  Rec #{s.recognition_number}
                                </span>
                              )}
                              {s.note && (
                                <span className={s.recognition_number ? 'ml-1.5' : ''}>
                                  {s.recognition_number ? '· ' : ''}
                                  {s.note}
                                </span>
                              )}
                            </>
                          )
                        }
                        badge={
                          <span className="inline-flex items-center justify-end gap-2 flex-wrap">
                            <VerifyBadge status={s.status} label={s.label} />
                            {s.url && <RecordLink url={s.url} />}
                          </span>
                        }
                      >
                        {s.quotes && s.quotes.length > 0 && (
                          <SourceQuote quotes={s.quotes} />
                        )}
                      </MatrixRow>
                    ))}
                </div>
              </div>
            )}

            {manifest.defense.regulations.length > 0 && (
              <div className="mb-4">
                <div className="text-[15px] font-semibold text-navy mb-2">
                  Cited regulations vs {manifest.defense.regulations[0]?.source ?? 'eCFR'} —{' '}
                  <VerifySummary
                    tally={manifest.defense.summary.regulations}
                    word="verified"
                  />
                </div>
                <div>
                  {[...manifest.defense.regulations]
                    .sort((a, b) => _verifyRank(a.status) - _verifyRank(b.status))
                    .map((r, i) => (
                      <MatrixRow
                        key={i}
                        headline={r.cfr ?? r.designation}
                        sub={
                          (r.name || r.note || r.pathway_flag) && (
                            <>
                              {r.name && <span>{r.name}</span>}
                              {r.note && <span className="text-muted">{r.note}</span>}
                              {r.pathway_flag && (
                                <span className="ml-1.5 font-semibold text-state-error-text whitespace-nowrap">
                                  ⚠ other pathway
                                </span>
                              )}
                            </>
                          )
                        }
                        badge={
                          <span className="inline-flex items-center justify-end gap-2 flex-wrap">
                            <VerifyBadge status={r.status} label={r.label ?? undefined} />
                            {r.url && <RecordLink url={r.url} />}
                          </span>
                        }
                      >
                        {r.quotes && r.quotes.length > 0 && (
                          <SourceQuote quotes={r.quotes} />
                        )}
                      </MatrixRow>
                    ))}
                </div>
              </div>
            )}

            {(manifest.defense.predicates?.length ?? 0) > 0 && (
              <div>
                <div className="text-[15px] font-semibold text-navy mb-2">
                  Predicate device(s) vs openFDA —{' '}
                  <VerifySummary
                    tally={manifest.defense.summary.predicates}
                    word="verified"
                  />
                </div>
                <div>
                  {manifest.defense.predicates.map((p, i) => (
                    <MatrixRow
                      key={i}
                      headline={<span className="font-mono">{p.k_number}</span>}
                      sub={p.detail || undefined}
                      badge={
                        <span className="inline-flex items-center justify-end gap-2 flex-wrap">
                          <VerifyBadge status={p.status} />
                          {p.url && <RecordLink url={p.url} />}
                        </span>
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      {/* USA 510(k)-exempt ONLY: Task 5's post-run grounding-verifier rows —
          the dossier's exemption determination + supporting prose, cross-checked
          against FDA's classification record. Absent for every other pathway. */}
      {manifest.exempt_grounding && manifest.exempt_grounding.length > 0 && (
        <div className="px-[22px] py-[16px] border-b">
          <div className="text-[16px] font-bold text-navy mb-1">
            Exemption grounding
          </div>
          <p className="text-[13px] text-muted mb-2 max-w-3xl">
            The dossier&rsquo;s 510(k)-exemption determination and supporting
            prose, cross-checked against FDA&rsquo;s classification record.
          </p>
          <div>
            {manifest.exempt_grounding.map((row: ExemptGroundingRow, i) => (
              <MatrixRow
                key={i}
                headline={exemptGroundingLabel(row.key)}
                sub={row.detail}
                badge={<VerifyBadge status={row.status} />}
              />
            ))}
          </div>
        </div>
      )}

      {/* Statements needing a source — factual claims that trace to nothing */}
      {manifest.unsourced && manifest.unsourced.length > 0 && (
        <div className="px-[22px] py-[16px] border-b">
          <div className="text-[16px] font-bold text-state-error-text mb-1">
            ⚠ Statements needing a source ({manifest.unsourced.length})
          </div>
          <p className="text-[13px] text-muted mb-2 max-w-3xl">
            These sentences state a specific figure but could not be traced to an
            uploaded document or the product account. Confirm the source before
            submission, or revise.
          </p>
          <div>
            {manifest.unsourced.map((u, i) => (
              <MatrixRow
                key={i}
                headline={u.value}
                sub={
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="rounded border border-state-error-border bg-state-error-bg px-1.5 py-0.5 font-mono font-medium text-state-error-text">
                      {u.contains}
                    </span>
                    <span className="text-muted">
                      {humanizeSectionKey(u.section_key)}
                    </span>
                  </span>
                }
                badge={
                  <span className="text-[13px] px-1.5 py-0.5 rounded bg-state-error-bg text-state-error-text border border-state-error-border whitespace-nowrap">
                    ⚠ for review
                  </span>
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Supporting detail — collapsed by default; the deep reference */}
      <details className="group border-t">
        <summary className="flex items-center gap-2 px-[22px] py-[14px] cursor-pointer select-none text-[14px] font-semibold text-navy marker:content-['']">
          <span className="text-muted transition-transform group-open:rotate-90">
            ▸
          </span>
          Supporting detail — section map &amp; full source list
        </summary>

        {/* Structural section → source map */}
        {sections.length > 0 && (
          <div className="px-[22px] py-[16px] border-t">
            <div className="text-[15px] font-semibold text-navy mb-2">
              Which sources fed each section
            </div>
            <div>
              {sections.map((key) => (
                <MatrixRow
                  key={key}
                  headline={humanizeSectionKey(key)}
                  sub={
                    <div className="flex flex-col gap-1.5">
                      {bySection.get(key)!.map(({ source, tier }, i) => (
                        <div key={i} className="flex items-center gap-2 flex-wrap">
                          <TierBadge tier={tier} />
                          <span className="text-navy">{source?.label ?? '—'}</span>
                        </div>
                      ))}
                    </div>
                  }
                />
              ))}
            </div>
          </div>
        )}

        {/* Source registry */}
        <div className="px-[22px] py-[16px] border-t">
          <div className="text-[15px] font-semibold text-navy mb-2">
            Sources ({manifest.sources.length})
          </div>
          <ul className="m-0 p-0 list-none flex flex-col gap-2">
            {manifest.sources.map((s) => (
              <li
                key={s.source_id}
                className="flex items-center gap-2 flex-wrap text-[15px]"
              >
                <span className="font-medium text-navy">{s.label}</span>
                {s.detail && (
                  <span className="text-[13px] text-muted">{s.detail}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </details>
    </section>
  );
}
